import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { emitToast } from '../../context/toast';
import { useI18n } from '../../context/useI18n';
import { Plus, TrendingUp } from 'lucide-react';

const TEST_COLUMNS = [
  { key: 'abdominales', labelKey: 'fitness_test.abdominales', fallback: 'Abdominales', unit: 'reps', hintKey: 'fitness_test.abdominales_hint', weightKey: 'abdominales_kg' },
  { key: 'burpees', labelKey: 'fitness_test.burpees', fallback: 'Burpees', unit: 'reps', hintKey: 'fitness_test.burpees_hint' },
  { key: 'flexiones', labelKey: 'fitness_test.flexiones', fallback: 'Flexiones', unit: 'reps' },
  { key: 'sentadilla', labelKey: 'fitness_test.sentadilla', fallback: 'Sentadilla', unit: 'reps', hintKey: 'fitness_test.abdominales_hint', weightKey: 'sentadilla_kg' },
  { key: 'plancha', labelKey: 'fitness_test.plancha', fallback: 'Plancha', unit: 'time' },
];

const REP_KEYS = TEST_COLUMNS.filter((c) => c.unit === 'reps').map((c) => c.key);

const emptyScores = () => Object.fromEntries(REP_KEYS.map((k) => [k, '']));
const emptyWeights = () => Object.fromEntries(
  TEST_COLUMNS.filter((c) => c.weightKey).map((c) => [c.weightKey, '']),
);

function formatPlancha(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0);
  if (!sec) return '—';
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}:${String(rem).padStart(2, '0')}`;
}

function splitPlancha(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0);
  if (!sec) return { min: '', sec: '' };
  return { min: String(Math.floor(sec / 60)), sec: String(sec % 60) };
}

function formatScoreCell(col, scores) {
  const value = scores?.[col.key];
  if (value == null || value === '') return '—';
  if (col.unit === 'time') return formatPlancha(value);
  const weight = col.weightKey ? scores?.[col.weightKey] : null;
  if (weight != null && Number(weight) > 0) {
    return `${value} (${weight} kg)`;
  }
  return String(value);
}

function formatCycleDate(iso, lang) {
  try {
    const dateOnly = String(iso).slice(0, 10);
    const [year, month, day] = dateOnly.split('-').map(Number);
    const local = new Date(year, month - 1, day, 12, 0, 0);
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'es-CO', {
      day: 'numeric',
      month: 'long',
    }).format(local);
  } catch {
    return iso;
  }
}

function ResultModal({ cycle, cycleDisplayName, onClose, onSaved }) {
  const { t } = useI18n();
  const displayName = cycleDisplayName || cycle?.cycle_display_name || cycle?.name || '';
  const [scores, setScores] = useState(() => {
    const base = emptyScores();
    const prev = cycle?.my_result?.scores || {};
    REP_KEYS.forEach((key) => {
      if (prev[key] != null) base[key] = String(prev[key]);
    });
    return base;
  });
  const [weights, setWeights] = useState(() => {
    const base = emptyWeights();
    const prev = cycle?.my_result?.scores || {};
    Object.keys(base).forEach((key) => {
      if (prev[key] != null) base[key] = String(prev[key]);
    });
    return base;
  });
  const [planchaMin, setPlanchaMin] = useState(() => splitPlancha(cycle?.my_result?.scores?.plancha).min);
  const [planchaSec, setPlanchaSec] = useState(() => splitPlancha(cycle?.my_result?.scores?.plancha).sec);
  const [burpeeType, setBurpeeType] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    let filled = 0;
    REP_KEYS.forEach((key) => {
      const raw = String(scores[key] ?? '').trim();
      if (raw !== '') {
        payload[key] = Number(raw);
        filled += 1;
      }
    });
    Object.entries(weights).forEach(([key, val]) => {
      const raw = String(val ?? '').trim();
      if (raw !== '') payload[key] = Number(raw);
    });
    const minPart = String(planchaMin ?? '').trim();
    const secPart = String(planchaSec ?? '').trim();
    if (minPart !== '' || secPart !== '') {
      const totalSec = (Number(minPart) || 0) * 60 + (Number(secPart) || 0);
      if (totalSec > 0) {
        payload.plancha = totalSec;
        filled += 1;
      }
    }
    if (filled === 0 && cycle?.my_result?.total) {
      emitToast({
        type: 'error',
        title: t('fitness_test.error', 'Error'),
        message: t('fitness_test.no_wipe', 'No se puede borrar el resultado. Deja al menos una medición o cancela.'),
      });
      return;
    }
    if (filled === 0) {
      emitToast({
        type: 'error',
        title: t('fitness_test.error', 'Error'),
        message: t('fitness_test.need_one', 'Ingresa al menos una medición.'),
      });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/fitness-tests/${cycle.id}/results`, { scores: payload, burpee_type: burpeeType || null });
      if (photoFile) {
        const form = new FormData();
        form.append('file', photoFile);
        form.append('caption', `${cycle.name} — ${t('fitness_test.photo_caption', 'Prueba física')}`);
        await api.post('/d28d/progress-photos', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      emitToast({ type: 'success', title: t('fitness_test.saved', 'Resultado guardado'), message: displayName });
      onSaved();
      onClose();
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('fitness_test.error', 'Error'),
        message: err.response?.data?.error || t('fitness_test.save_fail', 'No se pudo guardar'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-modal-overlay" onClick={onClose}>
      <form
        className="form-modal ui-light-panel surface-readable w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header shrink-0">
          <h3 className="text-lg font-bold text-stone-900">
            {t('fitness_test.modal_title', 'Registrar prueba')} — {displayName}
          </h3>
        </div>
        <div className="form-modal-content space-y-4 flex-1 min-h-0 overflow-y-auto">
        <p className="text-sm text-stone-600">
          {t('fitness_test.modal_hint', 'Ingresa repeticiones, peso en kg (opcional) y plancha en minutos y segundos.')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEST_COLUMNS.filter((col) => col.unit === 'reps').map((col) => (
            <div key={col.key} className="space-y-2 text-sm sm:col-span-2">
              <label className="block">
                <span className="font-semibold text-stone-900">
                  {t(col.labelKey, col.fallback)}
                </span>
                {col.hintKey ? <span className="block text-xs text-stone-500">{t(col.hintKey, '')}</span> : null}
                <input
                  type="number"
                  min="0"
                  className="input mt-1 w-full"
                  placeholder={t('fitness_test.reps_ph', 'Repeticiones')}
                  value={scores[col.key]}
                  onChange={(e) => setScores((s) => ({ ...s, [col.key]: e.target.value }))}
                />
              </label>
              {col.weightKey ? (
                <label className="block">
                  <span className="font-semibold text-stone-700 text-xs">
                    {t('fitness_test.weight_kg', 'Peso usado (kg)')}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="input mt-1 w-full"
                    placeholder={t('fitness_test.weight_ph', 'Ej. 10')}
                    value={weights[col.weightKey]}
                    onChange={(e) => setWeights((w) => ({ ...w, [col.weightKey]: e.target.value }))}
                  />
                </label>
              ) : null}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="font-semibold text-stone-900">{t('fitness_test.plancha_min', 'Plancha — minutos')}</span>
            <input
              type="number"
              min="0"
              className="input mt-1 w-full"
              placeholder="0"
              value={planchaMin}
              onChange={(e) => setPlanchaMin(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="font-semibold text-stone-900">{t('fitness_test.plancha_sec', 'Plancha — segundos')}</span>
            <input
              type="number"
              min="0"
              max="59"
              className="input mt-1 w-full"
              placeholder="0"
              value={planchaSec}
              onChange={(e) => setPlanchaSec(e.target.value)}
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="font-semibold text-stone-900">{t('fitness_test.burpee_type', 'Tipo de burpee (opcional)')}</span>
          <select className="input mt-1 w-full" value={burpeeType} onChange={(e) => setBurpeeType(e.target.value)}>
            <option value="">{t('fitness_test.burpee_any', '—')}</option>
            <option value="silla">{t('fitness_test.burpee_chair', 'Con silla')}</option>
            <option value="intermedio">{t('fitness_test.burpee_mid', 'Intermedio')}</option>
            <option value="completo">{t('fitness_test.burpee_full', 'Completo')}</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-semibold text-stone-900">{t('fitness_test.photo', 'Foto de evidencia (opcional)')}</span>
          <input
            type="file"
            accept="image/*"
            className="input mt-1 w-full"
            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
          />
        </label>
        </div>
        <div className="flex gap-2 form-modal-footer shrink-0">
          <button type="button" className="btn-secondary flex-1" onClick={onClose}>{t('common.cancel', 'Cancelar')}</button>
          <button type="submit" className="btn-primary flex-1" disabled={saving}>
            {saving ? '…' : t('fitness_test.save', 'Guardar resultado')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function FitnessTestPanel({ compact = false }) {
  const { t, lang } = useI18n();
  const [cycles, setCycles] = useState([]);
  const [cycleMeta, setCycleMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockedMsg, setBlockedMsg] = useState('');
  const [modalCycle, setModalCycle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setBlockedMsg('');
    try {
      const res = await api.get('/fitness-tests');
      setCycles(res.data?.data || []);
      setCycleMeta(res.data?.meta || null);
    } catch (err) {
      setCycles([]);
      setCycleMeta(null);
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 403 && msg) {
        setBlockedMsg(msg);
      } else if (status >= 500) {
        setBlockedMsg(t('fitness_test.server_error', 'No se pudieron cargar las pruebas. Intenta de nuevo en unos segundos.'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const withResults = cycles.filter((c) => c.my_result?.scores);
    if (!withResults.length) return null;
    const totals = withResults.map((c) => c.my_result.total || 0);
    const best = withResults.reduce((acc, c) => (
      (c.my_result?.total || 0) > (acc?.my_result?.total || 0) ? c : acc
    ), withResults[0]);
    return {
      count: withResults.length,
      avgTotal: Math.round(totals.reduce((a, b) => a + b, 0) / totals.length),
      bestCycle: best?.name,
      bestTotal: best?.my_result?.total || 0,
    };
  }, [cycles]);

  if (loading) {
    return <p className="d28d-text-muted text-sm">{t('fitness_test.loading', 'Cargando pruebas…')}</p>;
  }

  if (blockedMsg) {
    return (
      <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-[var(--d28d-text)]" role="alert">
        <p className="font-semibold">{t('fitness_test.pending_title', 'Pruebas no disponibles')}</p>
        <p className="mt-1 opacity-90">{blockedMsg}</p>
        <p className="mt-2 text-xs d28d-text-muted">
          {t('fitness_test.pending_hint', 'Cuando un administrador confirme tu pago en sede, podrás registrar medidas y resultados.')}
        </p>
      </div>
    );
  }

  const cycleClosed = cycleMeta?.cycle_ended === true;
  const premiacionEnrolled = cycleMeta?.premiacion_enrolled === true;

  return (
    <div className="space-y-6">
      {cycleClosed ? (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-[var(--d28d-text)]" role="alert">
          <p className="font-semibold">{t('fitness_test.cycle_closed_title', 'Ciclo cerrado')}</p>
          <p className="mt-1 opacity-90">
            {t('fitness_test.cycle_closed_hint', 'El ciclo actual ya terminó. No puedes registrar nuevas medidas hasta que inicie el próximo ciclo.')}
          </p>
        </div>
      ) : null}
      {cycleMeta?.premiacion_name ? (
        <div className={`rounded-xl border p-4 text-sm ${premiacionEnrolled ? 'border-lime-400/50 bg-lime-500/10' : 'border-stone-400/40 bg-stone-500/5'}`}>
          <p className="font-semibold text-[var(--d28d-text)]">
            {premiacionEnrolled
              ? t('fitness_test.premiacion_enrolled', 'Inscrito en premiación')
              : t('fitness_test.premiacion_not_enrolled', 'No inscrito en premiación')}
          </p>
          <p className="mt-1 d28d-text-muted">
            {premiacionEnrolled
              ? t('fitness_test.premiacion_enrolled_hint', 'Participas en {name}. Sube tus fotos en la pestaña Fotos o revisa la pestaña Premiación.', { name: cycleMeta.premiacion_name })
              : t('fitness_test.premiacion_not_enrolled_hint', 'Puedes inscribirte en {name} desde la pestaña Premiación.', { name: cycleMeta.premiacion_name })}
          </p>
        </div>
      ) : null}
      {!cycles.length && cycleMeta?.empty_reason === 'cycle_closed' ? (
        <p className="text-sm d28d-text-muted">
          {t('fitness_test.no_test_cycle_closed', 'No hay prueba abierta porque el ciclo está cerrado.')}
        </p>
      ) : null}
      {!compact && stats ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="card p-4">
            <div className="flex items-center gap-2 text-[var(--d28d-yellow)] mb-1">
              <TrendingUp size={18} />
              <span className="text-xs uppercase tracking-wide font-bold">{t('fitness_test.stats_cycles', 'Ciclos registrados')}</span>
            </div>
            <p className="text-2xl font-bold text-[var(--d28d-text)]">{stats.count}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide font-bold d28d-text-muted mb-1">{t('fitness_test.stats_avg', 'Promedio total')}</p>
            <p className="text-2xl font-bold text-[var(--d28d-text)]">{stats.avgTotal}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide font-bold d28d-text-muted mb-1">{t('fitness_test.stats_best', 'Mejor ciclo')}</p>
            <p className="text-lg font-bold text-[var(--d28d-text)]">{stats.bestTotal}</p>
            <p className="text-xs d28d-text-muted truncate">{stats.bestCycle}</p>
          </div>
        </div>
      ) : null}

      <div className="surface-readable overflow-x-auto rounded-xl border border-stone-300 shadow-sm">
        <table className="min-w-full text-sm fitness-test-table">
          <thead>
            <tr>
              <th className="px-3 py-2.5 text-left">{t('fitness_test.col_cycle', 'Ciclo / Prueba')}</th>
              {TEST_COLUMNS.map((col) => (
                <th key={col.key} className="px-3 py-2.5 text-center min-w-[5rem]">
                  {t(col.labelKey, col.fallback)}
                </th>
              ))}
              <th className="px-3 py-2.5 text-center">{t('fitness_test.col_total', 'Total')}</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {cycles.map((cycle, idx) => {
              const scores = cycle.my_result?.scores || {};
              const rowClass = idx % 2 === 1 ? 'row-readable-alt' : 'row-readable-base';
              return (
                <tr key={cycle.id} className={rowClass}>
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">
                    {formatCycleDate(cycle.test_date, lang)}
                  </td>
                  {TEST_COLUMNS.map((col) => (
                    <td key={col.key} className="px-3 py-2 text-center text-xs sm:text-sm">
                      {formatScoreCell(col, scores)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center font-bold">
                    {cycle.my_result?.total ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="btn-primary text-xs py-1 px-2 inline-flex items-center gap-1"
                      onClick={() => setModalCycle(cycle)}
                      disabled={cycleClosed}
                    >
                      <Plus size={12} />
                      {cycle.my_result ? t('fitness_test.edit', 'Editar') : t('fitness_test.register', 'Registrar')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalCycle ? (
        <ResultModal
          cycle={modalCycle}
          cycleDisplayName={cycleMeta?.cycle_name}
          onClose={() => setModalCycle(null)}
          onSaved={load}
        />
      ) : null}
    </div>
  );
}
